<template>
    <Head title="Feedback" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <div class="grid grid-flow-col gap-4">
                <div>
                    <h2 class="text-xl font-semibold leading-tight text-gray-800">
                        Feedback
                    </h2>
                </div>
            </div>
        </template>
        <form method="post" action="/admin/feedback/export">
            <input type="hidden" name="_token" :value="this.$page.props.csrf">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 gap-4 mt-4 flex items-end relative" v-if="feedback.data.length">
                <div>
                    <admin-app-label>Export Feedback</admin-app-label>
                    <select name="type" class="w-full">
                        <option v-for="option in exportOptions" v-bind:value="option.value">{{ option.text }}</option>
                    </select>
                </div>
                <div class="grid grid-rows-1 grid-flow-col gap-4">
                    <div class="grid grid-cols-3 gap-2">
                        <div class="w-full">
                            <admin-app-label>From</admin-app-label>
                            <input type="date" class="w-full" name="from" placeholder="From" required>
                        </div>
                        <div class="w-full">
                            <admin-app-label>To</admin-app-label>
                            <input type="date" class="w-full" name="to" placeholder="To" required>
                        </div>
                    </div>
                </div>
                <div>
                    <admin-link type="submit" class="inset-y-0 right-0 flex items-center h-full !py-3">Export</admin-link>
                </div>
            </div>
        </form>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="relative w-full my-6 overflow-hidden shadow">
            <form @submit.prevent="form.get('feedback')">
                <admin-search-form v-model="form.search"></admin-search-form>
            </form>
            <div class="px-2 md:px-6 pt-2 md:pt-4 pb-2">
                <div class="overflow-x-auto">
                    <div class="inline-block min-w-full overflow-hidden">
                        <table class="table flex table-auto w-full leading-normal">
                            <thead class="uppercase text-gray-600 text-xs font-semibold bg-gray-200">
                                <tr class="hidden lg:table-row">
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Name
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Email
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        <input-select
                                            multiple
                                            placeholder="Rating"
                                            :options="ratings"
                                            v-model="localRatings"
                                            @update:modelValue="applyFilter"
                                            :class="`!h-auto`"
                                        />
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Reason
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Contact
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Time
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Reference
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        <select v-model="selectedWebsite" @change="applyFilter">
                                            <option v-for="website in websites"
                                                    v-bind:value="website.value"
                                                    :disabled="website.disabled">{{ website.text }}
                                            </option>
                                        </select>
                                    </th>
                                    <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="my-3 flex-1 lg:flex-none">
                                <tr
                                    v-if="feedback.data.length"
                                    v-for="item of feedback.data"
                                    class="mb-2 lg:mb-0 border-2 lg:border-b-2 border-grey-200 lg:border-t lg:first:border-t-0 flex p-1 md:p-3 hover:bg-gray-100 lg:table-row flex-col w-full flex-wrap"
                                >
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.name }}
                                        <label @click="toggle(item.id)"
                                            v-if="item.comment"
                                            class="block tracking-wider uppercase cursor-pointer mt-2"><span v-if="showing === item.id"> <font-awesome-icon
                                            icon="minus"
                                            class="mr-3"/>Hide</span><span v-else><font-awesome-icon icon="plus"
                                                                                                     class="mr-3"/> Show</span>
                                            Comments</label>
                                        <div class="mt-2 p-3 border border-grey-200 transition duration-150 ease-in-out"
                                             v-if="showing === item.id">
                                            {{ item.comment }}
                                        </div>
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.email }}
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Rating</label>
                                        <ReviewStarRating :flex="`flex-0`"
                                                          :size="`1x`"
                                                          :modelValue="item.rating"></ReviewStarRating>
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.reason }}
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.contact }}
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.time ?? 'NA'}}
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                        {{ item.reference }}
                                    </td>
                                    <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                     <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Website</label>
                                        <span v-if="item.website">
                                            <a :href="`${item.website.url}`" target="_blank">
                                                <img :src="`/images/logos/tyre-sites/${item.website.name}.png`" class="object-scale-down w-20"/>
                                            </a>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-5 py-5 bg-white text-center lg:text-left text-sm md:text-base">
                                        <div class="flex items-center">
                                            <admin-link type="button"
                                                        class="bg-red"
                                                        @click="showDeleteModal(item.id)">
                                                Delete
                                            </admin-link>
                                            <admin-delete-modal v-if="deleteModalIdToShow === item.id"
                                                                :msg="modalMessage"
                                                                :type="modalType"
                                                                @toggle-delete-modal="hideDeleteModal()"
                                                                @delete-item="deleteItem(item.id)"></admin-delete-modal>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td class="py-6 bg-red" colspan="9" align="center">
                                        <p class="text-white font-bold">No feedback entries found</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <admin-pagination class="mt-6" :links="feedback.links" />
                    </div>
                </div>
            </div>
        </div>
        <ToTopLink>To Top</ToTopLink>
    </BreezeAuthenticatedLayout>
</template>
<script>
import BreezeAuthenticatedLayout from '@/Layouts/Admin/Authenticated.vue';
import { Inertia } from '@inertiajs/inertia';
import {Head, Link, useForm} from '@inertiajs/inertia-vue3';
import AdminLink from "@/Components/Admin/AdminLink";
import AdminSearchForm from "@/Components/Admin/Forms/AdminSearchForm";
import AdminDeleteModal from "@/Components/Admin/AdminDeleteModal";
import AdminPagination from "@/Components/Admin/AdminPagination";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import ReviewStarRating from "@/Components/Reviews/ReviewStarRating";
import Checkbox from "@/Components/Checkbox";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import ToTopLink from "@/Components/Admin/ToTopLink";
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";

export default {
    components:
        {
            ToTopLink,
            AdminAppLabel,
            BreadCrumbs,
            AdminPagination,
            AdminLink,
            AdminDeleteModal,
            AdminSearchForm,
            Checkbox,
            Head,
            InputSelect,
            FontAwesomeIcon,
            ReviewStarRating,
            Link,
            BreezeAuthenticatedLayout
        },
    data: function()
    {
        return {
            filter: '',
            showing: false,
            exportOptions: [
                { value: "all", text: "All Feedback" },
            ],
            ids: [],
            navItems: [
                { title: 'Feedback', path: '/admin/feedback' },
            ],
            deleteModalIdToShow: false,
            modalMessage: 'Are you sure you want to remove this feedback?',
            modalType: 'Feedback',
            localRatings: this.rating,
            ratings: ["1", "2", "3", "4", "5"],
            exportData: useForm({
                type : "",
                from: "",
                to: ""
            }),
            form: useForm({
                search: this.term
            }),
            selectedWebsite: this.website,
            websites: this.$trusted_checks.value ? [
                { value: '', text: 'Website' },
                { value: 'racshop', text: 'RAC Shop' }, 
                { value: 'racvhc', text: 'RAC Vehicle History Check' }
            ] : [
                { value: '', text: 'Website' },
                { value: 'asdatyres', text: 'ASDA Tyres' }, 
                { value: 'etyres', text: 'eTyres' },
                { value: 'ractyres', text: 'RAC Tyres' },
                { value: 'servicepoint', text: 'Service Point' }
            ]
        }
    },
    props: {
        feedback: Object,
        rating: String,
        term: String,
        website: String
    },
    methods: {
        toggle(id)
        {
            if(this.showing == id)
            {
                this.showing = false;
            }
            else
            {
                this.showing = id;
            }
        },
        applyFilter()
        {
            var params = {};
            if (this.term) {
                params.search = this.term;
            }
            if (this.selectedWebsite) {
                params.website = this.selectedWebsite;
            }
            if (this.localRatings) {
                params.ratings = this.localRatings;
            }

            var param_string = new URLSearchParams(params);
            if (param_string) {
                param_string = "?"+param_string;
            }

            Inertia.visit("/admin/feedback"+param_string);
        },
        showDeleteModal(id)
        {
            this.deleteModalIdToShow = id;
        },
        hideDeleteModal()
        {
            this.deleteModalIdToShow = false;
        },
        deleteItem(id)
        {
            this.$inertia.delete('/admin/feedback/'+id);
        }
    }
}
</script>
